/**
 * The repo's ONE flag-independent regex BOOLEAN test.
 *
 * Sibling of `const/regex-drain.ts` (`eachMatch`, the one exec-loop) and `const/regex-first-capture.ts`
 * (`firstCapture`, `collectFirstCaptures`, `collectCapturePairs`). Those two leaves own every OTHER consumption of a
 * caller-supplied pattern; the boolean verdict was the one member of the family with no home, so nine rule-helper
 * leaves had each spelled it inline and two of them carried a BYTE-IDENTICAL private copy under the same name.
 *
 * WHY IT IS SHARED AT ALL, and the defect it exists to prevent. A `/g` or `/y` RegExp carries `lastIndex` ON THE
 * OBJECT, and `test` both reads and advances it. A helper that receives a pattern from its CALLER cannot know the
 * flags, and every consuming rule holds its pattern at module scope and hands the SAME object in once per line, per
 * call or per file -- so an unreset test resumes wherever the previous one stopped and the match is silently missed.
 * For a compliance matcher that is a FALSE NEGATIVE: the rule under-fires and the scan reads CLEAN, which is the
 * expensive direction and the one no suite notices. The reset is one line and is exactly the line each re-spelling has
 * to remember.
 *
 * NOT the class `pack-44-gflag-single-exec-lastindex.md` convicts, and the boundary is the whole reason this leaf is
 * narrow. That pack's subject is a MODULE-LEVEL regex carrying `g` for no reason, whose adjudicated cure is to DELETE
 * THE FLAG at the declaration -- and it names "a reset added WITHOUT deleting the flag" as the symptom-patch it exists
 * to convict. Adopting this helper THERE would be that patch. Here the pattern is a PARAMETER: its flags belong to a
 * caller in another file, deleting them is not this module's move, and the pack itself carves the case out -- "a reset
 * inside a helper can serve loop callers the needle cannot see". Root cause and symptom are opposite sides of that
 * line, which is why the paired forbid-rule `QUAL-INLINE-FLAGLESS-REGEX-TEST-001` fires ONLY where the tested
 * identifier resolves to the enclosing function's PARAMETER list.
 *
 * LAYER FLOOR. Its consumers are `utils/compliance/rule-helpers/` leaves today and rule leaves under
 * `tools/code-scanner/compliance/` tomorrow, and neither root can host it: a `utils/` file may value-import nothing but
 * `@federa/const`, `@federa/types` and `zod` (`QUAL-NO-RUNTIME-IMPORTS-IN-UTILS-001`, no allowlist) and a `tools/` file
 * may import no `tools/` sibling (`QUAL-NO-CROSS-TOOL-IMPORTS-001`). `const/` is the only layer both reach -- the same
 * measured floor `regex-drain.ts` and `regex-first-capture.ts` record for themselves.
 */

/**
 * True when `pattern` matches `text`, WHATEVER flags `pattern` carries.
 *
 * @runtime-derived-ssot-helper (const-leaf-pinned): the one flag-independent boolean test every rule-helper DI seam is
 * defined in terms of. Pinned with its leaf for the layer reason in the module docstring.
 *
 * ARGUMENT ORDER IS SUBJECT-FIRST, matching the scanning family it joins (`collectFirstGroup1PerLine`,
 * `collectMatchingLineIndices`, `collectFirstCaptures`, `eachMatch`) rather than the two private `testAt` copies it
 * folds, which spelled `(pattern, text)`. The flip is deliberate and it is SAFE BY CONSTRUCTION rather than by review:
 * the two parameters have disjoint types, so a site adopting this helper in the old order fails to COMPILE instead of
 * silently testing a pattern against a pattern.
 */
export function testFlagIndependent(text: string, pattern: RegExp): boolean {
	pattern.lastIndex = 0;
	return pattern.test(text);
}

/**
 * True when ANY member of `patterns` matches `text` -- the COLLECTION form of {@link testFlagIndependent}, and the
 * same decision one level up.
 *
 * WHY IT IS A SECOND EXPORT RATHER THAN A CALL SITE'S `.some(...)`. The scalar helper above cured nine leaves that had
 * each spelled `pattern.lastIndex = 0; return pattern.test(text)` inline; the collection spelling grew the SAME way and
 * was at THREE bodies when this was minted -- a module-private `matchesAnyPattern(haystack, patterns)` in
 * `utils/wrangler/cf-session-detectors.ts`, a suite-private `matchesAny(patterns, text)` in
 * `const/__tests__/ws-error-message-patterns.test.ts` (argument order FLIPPED against its sibling, which is how the
 * pair reads as two decisions), and two fully inline spellings in `utils/websocket/sanitize-error-message.ts`. One
 * name, three bodies, two argument orders: the `QUAL-PRIVATE-FN-SHADOWS-SHARED-EXPORT-001` shape with no shared export
 * to shadow yet.
 *
 * IT DELEGATES rather than re-spelling `.test`, so the reset is decided ONCE. That matters exactly as much here as it
 * does above: a caller holding a MODULE-LEVEL pattern array drives the same RegExp objects on every call, so a `/g`
 * member of such an array under-matches from the second call onward and the miss is a silent FALSE NEGATIVE.
 *
 * INERT AT EVERY LIVE CARRIER, measured rather than assumed: no pattern in `CF_SESSION_FAILURE_PATTERNS`,
 * `WRANGLER_AUTH_ERROR_PATTERNS`, `INTERNAL_ERROR_PATTERNS` or `USER_FACING_ERROR_PATTERNS` carries `g` or `y`, so
 * every adoption is a pure re-binding. That inertness is also the answer to
 * `pack-44-gflag-single-exec-lastindex.md`, which convicts "a reset added WITHOUT deleting the flag" as a
 * symptom-patch: no flag is being patched over here, because no carrier has one. If a future caller passes a `/g`
 * array, the root-cause cure is still to DELETE THE FLAG at that declaration -- this helper makes the walk total, it
 * does not license the flag.
 *
 * ARGUMENT ORDER IS SUBJECT-FIRST, matching {@link testFlagIndependent} and the scanning family it joins, rather than
 * the suite-private copy that spelled `(patterns, text)`. Safe by construction rather than by review: the parameters
 * have disjoint types, so a site adopting the old order fails to COMPILE.
 *
 * @runtime-derived-ssot-helper (const-leaf-pinned): shares the layer floor its scalar sibling records -- a `utils/`
 * leaf may value-import nothing but `@federa/const`, `@federa/types` and `zod`, so no `utils/` home reaches the
 * `utils/websocket` and `utils/wrangler` consumers at once.
 */
export function matchesAnyPattern(text: string, patterns: readonly RegExp[]): boolean {
	return patterns.some((pattern) => testFlagIndependent(text, pattern));
}

/**
 * True when `screen` is ABSENT or matches `text` -- an OPTIONAL screen admits everything when its caller had none to
 * build: the whole-word pre-screen a non-word field name cannot have (`QUAL-ENVELOPE-FIELD-NO-READER-001`), the
 * installer call a registration outside any exported installer cannot require
 * (`QUAL-RUN-RECORD-TRANSPORT-KIND-UNREGISTERED-001`). Both spelled `screen === null || screen.test(text)` inline.
 * @runtime-derived-ssot-helper (const-leaf-pinned): the optional form of {@link testFlagIndependent}, same floor.
 */
export function passesOptionalScreen(text: string, screen: RegExp | null): boolean {
	return screen === null || testFlagIndependent(text, screen);
}
